import { Skeleton } from '@chakra-ui/react';
import React, { useState, useEffect } from 'react';

import { CreditAgreement, Insights } from '@finley-technologies/finley-types';

import {
  chartsInitialEndDateJs,
  CohortConfig,
  defaultCohortCadence,
  defaultMaxSeriesAge,
  onPortfolioVintageChartFilterUpdate,
  onSelectDateRange,
  PortfolioChartConfig,
  ProfitsVintageInsight,
  ProfitsVintageTableRow,
  vintageInitialStartDate,
  vintageInitialStartDateJs,
} from '../shared/portfolioChartUtils';
import { VintageChartTemplate } from './VintageChartTemplate';
import { getDefaultDashboardDisplayEndDate } from '~/packages/common-frontend/constants';
import { useGetProfitVintageInsightsQuery } from '~/packages/portfolio/requests/requests.gql.generated';

type ProfitLossVintageChartTemplateProps = {
  creditFacilityId: string;
  chartTitle: string;
  field: keyof Insights.ProfitGeneratedCohortInsightsData;
  fieldType: CreditAgreement.DataTapeField.Type;
  yAxisLabel?: string;
};
export const ProfitLossVintageChartTemplate = ({
  creditFacilityId,
  chartTitle,
  field,
  fieldType,
  yAxisLabel,
}: ProfitLossVintageChartTemplateProps) => {
  const [
    selectedDatapoint,
    setSelectedDatapoint,
  ] = useState<ProfitsVintageInsight | null>(null);
  const [chartDataDateRange, setChartDataDateRange] = useState<{
    chartDataStartDate: Date;
    chartDataEndDate: Date;
  }>({
    chartDataStartDate: vintageInitialStartDateJs,
    chartDataEndDate: chartsInitialEndDateJs,
  });

  // need both initial date range chart data (returned by query)
  // as well as chart data for time period
  // because we only want to filter chart data for time period on top of initial date range chart data
  // and not include a previously-filtered time period
  const [
    profitVintageInputDataForDateRange,
    setProfitVintageInputDataForDateRange,
  ] = useState<Insights.CreditFacilityProfitVintageInputInsightsData[]>([]);
  const [chartDataWithCohortCadence, setChartDataWithCohortCadence] = useState<
    ProfitsVintageInsight[]
  >([]);
  const [tableDataByOriginationDate, setTableDataByOriginationDate] = useState<
    ProfitsVintageTableRow[]
  >([]);

  const [activeFields, setActiveFields] = useState<
    Partial<keyof ProfitsVintageInsight>[]
  >([]);

  const [chartConfig, setChartConfig] = useState<PortfolioChartConfig<string>>(
    {},
  );

  // Cohort-specific states, powers chart filters
  const [
    selectedCohortConfig,
    setSelectedCohortConfig,
  ] = useState<CohortConfig>({
    cohortBaseCadence: defaultCohortCadence,
    cohortSeriesCadence: defaultCohortCadence,
  });
  const [selectedMaxSeriesAge, setSelectedMaxSeriesAge] = useState<number>(
    defaultMaxSeriesAge,
  );

  // we generate new cohort data when filter selections are changed
  // using the base input daily cohort data for the selected dates
  // all chart filters for vintage aside from the date range picker are run on client without API calls
  useEffect(() => {
    onPortfolioVintageChartFilterUpdate({
      profitVintageInputDataForDateRange,
      selectedCohortConfig,
      selectedMaxSeriesAge,
      field,
      fieldType: 'percentage',
      setChartConfig,
      setActiveFields,
      setChartDataWithCohortCadence,
      setTableDataByOriginationDate,
      setSelectedDatapoint,
    });
  }, [
    field,
    profitVintageInputDataForDateRange,
    selectedCohortConfig,
    selectedMaxSeriesAge,
  ]);

  const {
    refetch: refetchProfitVintageInsights,
    data: profitVintageInsightsData,
    loading: isProfitVintageInsightsLoading,
  } = useGetProfitVintageInsightsQuery({
    variables: {
      creditFacilityParams: {
        creditFacilityId,
        reportingDateStart: vintageInitialStartDate,
        reportingDateEnd: getDefaultDashboardDisplayEndDate(),
      },
    },
    onCompleted: (data) => {
      const inputData = data.getProfitVintageInsights.profitVintageInputData;

      setProfitVintageInputDataForDateRange(
        inputData as Insights.CreditFacilityProfitVintageInputInsightsData[],
      );
    },
    notifyOnNetworkStatusChange: true,
  });

  if (isProfitVintageInsightsLoading || !profitVintageInsightsData) {
    return <Skeleton height="700px" />;
  }

  return (
    <VintageChartTemplate
      setSelectedMaxSeriesAge={setSelectedMaxSeriesAge}
      tableDataByOriginationDate={tableDataByOriginationDate}
      selectedCohortConfig={selectedCohortConfig}
      setSelectedCohortConfig={setSelectedCohortConfig}
      chartTitle={chartTitle}
      activeFields={activeFields}
      setActiveFields={setActiveFields}
      selectedDatapoint={selectedDatapoint}
      setSelectedDatapoint={setSelectedDatapoint}
      yAxisLabel={yAxisLabel}
      chartConfig={chartConfig}
      chartDataWithCohortCadence={chartDataWithCohortCadence}
      chartDataDateRange={chartDataDateRange}
      setChartDataDateRange={setChartDataDateRange}
      fieldType={fieldType}
      onSelectDateRangeHandler={(newDateRange) =>
        onSelectDateRange({
          newDateRange,
          refetch: refetchProfitVintageInsights,
          creditFacilityId,
          setChartDataDateRange,
        })
      }
      showOriginatedReceivablesCount={false}
    />
  );
};
